import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'loanCapacity',
    'contribution',
    'rateInput',
    'rateSpan',
    'notaryFees',
    'warrantyFees',
    'totalAmount',
    'propertySlider',
    'propertySliderTitle',
    'buyerAlertLink',
    'buyerAlertLinkLabel',
    'reimburseInput',
    'familyIncomeInput',
    'currentChargesInput',
    'reimbursementCapacity',
    'reimbursementCapacityTooHigh',
    'durationSlider'
  ];

  static values = {
    interestRatesFifteen: Number,
    interestRatesTwenty: Number,
    interestRatesTwentyFive: Number
  }

  connect() {
    this.initialValues = {
      familyIncome: '0',
      contribution: '0',
      duration: '20',
      rate: this.interestRatesTwentyValue,
      currentCharges: '0',
      reimburse: '0'
    };

    this.formValues = { ...this.initialValues }
    this.lastFetchedPrice = 0
  }

  updateInputValue(event) {
    const { name, value } = event.target
    let formattedValue = value

    if (name === 'duration') {
      this.handleRateElements(value)
      const parsedValue = parseInt(value)
      this.durationSliderTarget.value = parsedValue
    }

    if (['currentCharges', 'familyIncome'].includes(name)) this.calculateReimbursementCapacity()

    this.formValues[name] = formattedValue || this.initialValues[name]
    this.calculateLoanCapacity()
  }

  calculateLoanCapacity() {
    const { contribution, duration, rate } = this.formValues;

    const reimbursementCapacity = this.formatNumber(this.formValues.reimburse)
    const monthlyRate = parseFloat(rate) / 100 / 12;
    if (monthlyRate <= 0) return

    const numberOfMonths = this.formatNumber(duration) * 12;
    const borrowableAmount = reimbursementCapacity * (1 - Math.pow(1 + monthlyRate, -numberOfMonths)) / monthlyRate;
    const totalAmount = Math.max(0, borrowableAmount + this.formatNumber(contribution));
    const notaryFees = totalAmount * 0.075;
    const warrantyFees = borrowableAmount * 0.01;
    const totalFees = notaryFees + warrantyFees;
    const netAmount = Math.max(0, borrowableAmount + this.formatNumber(contribution) - totalFees);

    this.loanCapacityTarget.innerText = `${this.roundNumber(borrowableAmount).toLocaleString()} €`
    this.contributionTarget.innerText = `${this.roundNumber(this.formatNumber(contribution)).toLocaleString()} €`
    this.notaryFeesTarget.innerText = `${this.roundNumber(notaryFees).toLocaleString()} €`
    this.warrantyFeesTarget.innerText = `${this.roundNumber(warrantyFees).toLocaleString()} €`
    this.totalAmountTarget.innerText = `${this.roundNumber(netAmount).toLocaleString()} €`

    this.updatePropertySlider(netAmount);
    this.updateBuyerAlertLinks(netAmount);
  }

  calculateReimbursementCapacity() {
    const income = this.formatNumber(this.familyIncomeInputTarget.value) || 0;
    const charges = this.formatNumber(this.currentChargesInputTarget.value) || 0;
    const remainingRepaymentCapacityMax = income * 0.35
    const remainingRepaymentCapacityLeft = remainingRepaymentCapacityMax - charges

    const debtRatio = (charges / income) * 100;

    let finalRepayment = 0
    if (debtRatio > 35) {
      this.reimburseInputTarget.value = finalRepayment
      this.formValues.reimburse = finalRepayment
      this.reimbursementCapacityTarget.innerText = '0'
      this.reimbursementCapacityTooHighTarget.classList.remove('hidden')
    } else {
      finalRepayment = Math.round(remainingRepaymentCapacityLeft * 100) / 100;
      this.reimburseInputTarget.value = finalRepayment.toLocaleString()
      this.formValues.reimburse = finalRepayment
      this.reimbursementCapacityTarget.innerText = `${this.roundNumber(remainingRepaymentCapacityLeft).toLocaleString()}`
      this.reimbursementCapacityTooHighTarget.classList.add('hidden')
    }
  }

  async updatePropertySlider(price) {
    try {
      if (Math.abs(this.lastFetchedPrice - price) < 99_000) return;

      const data = await this.fetchContent(price);
      this.propertySliderTarget.innerHTML = data
    } catch (error) {
      console.log('error fetching sales')
    } finally {
      if (Math.abs(this.lastFetchedPrice - price) > 99_000) {
        this.propertySliderTitleTarget.innerHTML = `<h2 class='h2 text-center md:w-2/3 mx-auto'>Découvrez nos derniers biens à vendre <span class='text-ih-blue'>correspondants à votre budget de ${this.roundNumber(price).toLocaleString()} €</span></h2>`
      } else {
        this.propertySliderTitleTarget.innerHTML = "<h2 class='h2 text-center md:w-2/3 mx-auto'>Découvrez nos derniers <span class='text-ih-blue'>biens à vendre</span></h2>"
      }
    }
  }

  updateBuyerAlertLinks(price) {
    this.buyerAlertLinkTargets.forEach(link => {
      link.href = price > 1_000 ? `${link.href.split('?')[0]}?maxBudget=${this.roundNumber(price)}` : link.href.split('?')[0]
    });

    this.buyerAlertLinkLabelTargets.forEach(label => {
      label.innerText = price > 1_000 ?  'Créer une alerte avec ce budget' : 'Créer une alerte achat';
    });
  }

  // private

  async fetchContent(price) {
    const response =  await fetch(`${window.location.origin}/sale_sliders?min_price=${price*0.9}&max_price=${price}`, {
      method: 'GET',
      headers: {
        Accept: 'text/html',
        'Content-Type': 'application/json'
      }
    });

    return await response.text();
  }

  formatNumber(value) {
    const number = parseFloat(value.toString().replace(/\s+/g, '').replace(/\D/g, ''));

    return isNaN(number) ? 0 : number;
  }

  roundNumber(value) {
    const number = (Math.round(value) / 10) * 10;

    return isNaN(number) ? 0 : number;
  }

  handleRateElements(duration) {
    const defaultValue = this.getAvgRate(duration)

    this.rateInputTarget.value = defaultValue
    this.rateSpanTarget.innerText = `${defaultValue} %`
    this.formValues.rate = defaultValue
  }

  getAvgRate(duration) {
    if (duration <= 15) return this.interestRatesFifteenValue;
    else if (duration <= 20) return this.interestRatesTwentyValue;
    else return this.interestRatesTwentyFiveValue;
  }
}
